import { getFullStoryText } from './storyContent'

/** Shape shared by a freshly loaded story and the in-memory copy App holds. */
interface StoryStartInput {
  metadata?: { onboarded?: boolean }
  chapters?: StoryChapter[]
  conversations?: Conversation[]
}

/** Anything at all in any chapter. */
export function hasManuscriptText(data: StoryStartInput): boolean {
  return getFullStoryText(data.chapters ?? []).trim() !== ''
}

/**
 * Whether the writer and the agent have actually said anything to each other. A story is
 * created with an empty conversation already attached, so the presence of a conversation
 * means nothing — only a message someone sent or received does.
 */
export function hasAgentConversation(data: StoryStartInput): boolean {
  return (data.conversations ?? []).some((conversation) =>
    (conversation.messages ?? []).some(
      (message) => message.role === 'user' || message.role === 'assistant'
    )
  )
}

/**
 * A story nobody has started yet: no wizard run, nothing written, nothing said. Decides
 * whether opening the story also opens the chat panel.
 */
export function isStoryUntouched(data: StoryStartInput): boolean {
  if (data.metadata?.onboarded === true) return false
  return !hasManuscriptText(data) && !hasAgentConversation(data)
}

/**
 * Whether opening this story should offer the guided wizard.
 *
 * Narrower than it might look: EITHER signal of having started is enough to suppress the
 * offer, not just a finished manuscript. A story that has been chatted at — even with an
 * empty manuscript — is a story already underway: the conversation *is* the setup. Offering
 * the guided wizard there is offering to redo work in progress, not to help someone get
 * started. Only a story with neither prose nor a real exchange gets the offer.
 *
 * It is re-evaluated on every open rather than remembered, so declining and coming back to a
 * story that still has not got going offers again — that return trip is the moment the offer
 * is most likely to be wanted.
 *
 * `onboarded` is the one hard stop. It means either the wizard already ran for this story
 * or prose has been saved to it (the save route sets it on the first non-empty chapter),
 * and re-offering setup in either case is offering to redo work already done.
 */
export function shouldOfferWizard(data: StoryStartInput): boolean {
  if (data.metadata?.onboarded === true) return false
  return !hasManuscriptText(data) && !hasAgentConversation(data)
}

/**
 * A conversation is unread when the agent has said something in it since the writer last
 * looked: some assistant message postdates `lastReadAt` (or nothing has ever been read).
 * A writer's own message never counts — sending a message you already know you sent is not
 * something you need to be told about.
 *
 * `lastReadAt === null` reading as unread is safe against a flood of stale history: the
 * server-side migration that introduces `last_read_at` backfills every pre-existing
 * conversation to the migration time in the same transaction that adds the column (see
 * src/server/index.ts), so a null only ever occurs on a conversation created after that
 * migration ran — and a brand-new conversation has no reply yet to be unread about. Null
 * genuinely means "never read", not "read before tracking existed".
 */
export function isConversationUnread(conversation: Conversation): boolean {
  const readAt = conversation.lastReadAt ? Date.parse(conversation.lastReadAt) : 0
  return (conversation.messages ?? []).some(
    (message) => message.role === 'assistant' && message.timestamp > readAt
  )
}

/**
 * The most recently replied-to unread conversation, or null if nothing is unread.
 */
export function latestUnreadConversation(conversations: Conversation[]): Conversation | null {
  let best: Conversation | null = null
  let bestTimestamp = -Infinity
  for (const conversation of conversations) {
    if (!isConversationUnread(conversation)) continue
    const newest = (conversation.messages ?? []).reduce(
      (max, message) => Math.max(max, message.timestamp),
      -Infinity
    )
    if (newest > bestTimestamp) {
      bestTimestamp = newest
      best = conversation
    }
  }
  return best
}

/** How long a conversation counts as "the one you were just in" after its last message. */
export const RECENT_CONVERSATION_WINDOW_MS = 6 * 60 * 60 * 1000

/**
 * The conversation the writer was most recently in, if it was recent enough to resume:
 * the one whose newest user or assistant message falls within the last six hours. Coming
 * back to a story from the home page or from another story should land on the chat you
 * just left, not on a fresh empty one -- but only while that chat is still plausibly "in
 * progress". Past the window a new chat is the better default. System messages do not
 * count: nobody chose to send them, so they say nothing about where the writer was.
 */
export function latestRecentConversation(
  conversations: Conversation[],
  now: number = Date.now(),
  windowMs: number = RECENT_CONVERSATION_WINDOW_MS
): Conversation | null {
  let best: Conversation | null = null
  let bestTimestamp = -Infinity
  for (const conversation of conversations) {
    const newest = (conversation.messages ?? []).reduce(
      (max, message) =>
        message.role === 'user' || message.role === 'assistant'
          ? Math.max(max, message.timestamp)
          : max,
      -Infinity
    )
    if (newest <= now - windowMs) continue
    if (newest > bestTimestamp) {
      bestTimestamp = newest
      best = conversation
    }
  }
  return best
}
